import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useOrgFeatures } from '../hooks/useOrgFeatures';
import AccessCheckFailed from './AccessCheckFailed';

function ExternalIntegrationGate({
  children,
  requiredIntegration,
  requiredAnyIntegration,
  fallbackPath,
  onRetry,
}) {
  const { hasExternalIntegration, loading, error } = useOrgFeatures();

  if (loading) {
    return (
      // Same quiet spinner as PermissionProtectedRoute: the stacked route guards
      // used to flash a different caption each in turn.
      <div role="status" className="flex h-screen items-center justify-center">
        <div aria-hidden="true" className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-teal-600 dark:border-gray-700 dark:border-t-teal-500" />
        <span className="sr-only">Verifying integration access...</span>
      </div>
    );
  }

  // A failed load (5xx, network error) leaves the integration map empty, which
  // looked exactly like "not enabled" and bounced the user to the Dashboard.
  // Unknown is not denied: say so and offer a retry.
  if (error) return <AccessCheckFailed onRetry={onRetry} />;

  const allowed = Array.isArray(requiredAnyIntegration)
    ? requiredAnyIntegration.some((provider) => hasExternalIntegration(provider))
    : hasExternalIntegration(requiredIntegration);

  if (!allowed) {
    return <Navigate to={fallbackPath} replace />;
  }

  return children;
}

export default function ExternalIntegrationProtectedRoute({
  children,
  requiredIntegration,
  requiredAnyIntegration,
  fallbackPath = '/admin',
}) {
  // useOrgFeatures loads once per mount, so Retry remounts the gate to refetch.
  const [attempt, setAttempt] = useState(0);
  return (
    <ExternalIntegrationGate
      key={attempt}
      requiredIntegration={requiredIntegration}
      requiredAnyIntegration={requiredAnyIntegration}
      fallbackPath={fallbackPath}
      onRetry={() => setAttempt((n) => n + 1)}
    >
      {children}
    </ExternalIntegrationGate>
  );
}
